/**
 * formatPublishedDate — the ONE blog publication-date formatter (BlogPostCard, BlogPostPage).
 *
 * BLOG-STD-001: the locale is pinned and the zone is UTC so the server-prerendered string and the
 * client hydration render byte-identically (the visitor's own zone must not change the date text
 * and trip a hydration mismatch).
 *
 * ES-I18N-FOLLOW-ON (2026-08-02): this was `.toLocaleDateString(locale || 'en-US', …)`. Two defects
 * in one line — the METHOD reads the RUNTIME locale whenever its first argument is nullish
 * (indistinguishable by sight from a correct call), and the `|| 'en-US'` silently rendered English
 * for any caller that forgot to thread one. `Intl.DateTimeFormat` cannot be written without a
 * locale, so the omission becomes visible instead.
 *
 * SWALLOWED-CATCH-BURNDOWN-001 U3 (2026-10-09): two copies of this function each wrapped the whole
 * body in a catch that answered the raw ISO string for ANY failure. The one expected failure — a
 * stored value that is not a date — is now ASKED (`Number.isNaN`) rather than caught, and shown as
 * stored. The only other throw, a `RangeError` from a locale tag `Intl` cannot read, is a defect in
 * the caller's wiring and surfaces.
 *
 * @module @puffin-core/marketing-shell/blog/formatPublishedDate
 */

/**
 * @param {string} iso    the stored publication instant
 * @param {string} [locale]  BCP 47 tag of the page's language
 * @returns {string} the date in the page's language, or the stored text when it is not a date
 */
export default function formatPublishedDate(iso, locale) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}
